import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate, STATUS, FLAGS } from "@/lib/constants";
import { useLang } from "@/i18n";
import { RentalBlock } from "@/components/dashboard/RentalBlock";

export default function PlanningDetailDialog({ detail, onClose, period, setPeriod, savePeriod,
  chain, chainCheckin, doMerge }) {
  const { t, lang } = useLang();
  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="planning-detail-dialog">
        <DialogHeader><DialogTitle className="font-display">{detail?.guest_name} — {detail?.pitch_label}</DialogTitle></DialogHeader>
        {detail && (
          <div className="space-y-2 text-sm">
            <p className="font-display font-bold text-lg text-primary">{fmtDate(detail.arrival)} → {fmtDate(detail.departure)}</p>
            <div className="grid grid-cols-2 gap-2 text-[13px]">
              <p><span className="text-muted-foreground">{t("status")}: </span>{detail.status === "checked_in" ? (STATUS.bezet[lang] || "Bezet") : (STATUS.gereserveerd[lang] || "Gereserveerd")}</p>
              <p><span className="text-muted-foreground">{t("persons")}: </span>{detail.persons}</p>
              {detail.company && <p className="col-span-2"><span className="text-muted-foreground">{t("company")}: </span>{detail.company}</p>}
              {detail.email && <p className="col-span-2"><span className="text-muted-foreground">{t("email")}: </span>{detail.email}</p>}
              {detail.phone && <p><span className="text-muted-foreground">{t("phone")}: </span>{detail.phone}</p>}
              {detail.license_plate && <p><span className="text-muted-foreground">{t("license_plate")}: </span>{detail.license_plate}</p>}
              {detail.notes && <p className="col-span-2 text-muted-foreground">{detail.notes}</p>}
            </div>
            {detail.rood_lintje && <p className="text-rose-700 font-semibold text-xs">{FLAGS.rood_lintje[lang] || FLAGS.rood_lintje.nl}</p>}

            {detail.status !== "checked_out" && (
              <div className="rounded-lg border border-border bg-card p-3" data-testid="period-box">
                <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">{t("change_period")}</p>
                <div className="flex items-end gap-2">
                  <div className="space-y-1 flex-1"><Label className="text-xs">{t("arrival")}</Label>
                    <Input type="date" value={period.arrival} onChange={(e) => setPeriod({ ...period, arrival: e.target.value })} data-testid="period-arrival" /></div>
                  <div className="space-y-1 flex-1"><Label className="text-xs">{t("departure")}</Label>
                    <Input type="date" value={period.departure} onChange={(e) => setPeriod({ ...period, departure: e.target.value })} data-testid="period-departure" /></div>
                  <Button size="sm" onClick={savePeriod} data-testid="period-save"
                    disabled={!period.arrival || !period.departure || period.departure <= period.arrival
                      || (period.arrival === detail.arrival && period.departure === detail.departure)}>{t("save")}</Button>
                </div>
              </div>
            )}

            <RentalBlock reservationId={detail.reservation_id} start={detail.arrival} end={detail.departure} lang={detail.language || "nl"} />
            {chain.length > 1 && (
              <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3 space-y-1" data-testid="chain-box">
                <p className="text-[11px] font-bold uppercase tracking-wider text-sky-800">{t("linked_stays")}</p>
                {chain.map((c) => (
                  <p key={c.id} className="text-[12px] text-foreground/90 flex items-center gap-2" data-testid={`chain-seg-${c.id}`}>
                    <span className="h-2 w-2 rounded-full" style={{ background: c.status === "checked_in" ? "#e11d48" : "#f59e0b" }} />
                    <b>{c.pitch_label}</b> {fmtDate(c.arrival)} → {fmtDate(c.departure)}
                  </p>
                ))}
                {chain.some((c) => c.status !== "checked_in") && chain.some((c) => c.status === "checked_in") && (
                  <Button size="sm" className="mt-1 h-7" onClick={chainCheckin} data-testid="chain-checkin">{t("chain_all_booking")}</Button>
                )}
                {chain.filter((c) => c.pitch_label === detail.pitch_label).length > 1 && (
                  <Button size="sm" variant="outline" className="mt-1 ml-2 h-7" onClick={doMerge} data-testid="chain-merge">{t("merge_stays")}</Button>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
